// src/components/FinancialDashboard.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  DollarSign,
  TrendingUp,
  Target,
  Shield,
  CreditCard,
  Briefcase,
  Printer,
  User,
  Loader,
  Wallet,
  Sparkles,
  Search,
  Menu,
  Gauge,
  Zap,
  Star,
} from 'lucide-react';

import authService from '../authentication/AuthService';
import { useTheme } from '../hooks/useTheme';
import Header from './Header';
import SidebarNav from './SidebarNav';

/* ─── dashboard tabs ──────────────────────────────────────────────────── */
import OverviewTab      from './OverviewTab';
import ProfileTab       from './ProfileTab';
import RiskAssessmentTab from './RiskAssessmentTab';
import DebtTab          from './DebtTab';

import ExpensesTab      from './ExpensesTab';
import GoalsTab         from './GoalsTab';
import IncomeTab        from './IncomeTab';
import InvestmentsTab   from './InvestmentsTab';
import NetIncomeTab     from './NetIncomeTab';
import PrintView        from './PrintView';

import DCATab from "./DCATab.js"
import RetirementTab from "./RetirementTab";
import WalletsTab from "./WalletsTab";
import ToolsTab from "./ToolsTab";
import ResearchTab from "./research/ResearchTab";
import useResearchFlags from "../hooks/useResearchFlags";
import StrategyAutomationTab from "./strategyautomation/StrategyAutomationTab";
import ComingSoonCard from "./strategyautomation/ComingSoonCard";
import useStrategyAutomationFlag from "../hooks/useStrategyAutomationFlag";
import SupernovaTab from "./supernova/SupernovaTab";
import { isSupernovaTabEnabled } from "../config/supernovaFlags";
import {
  reconcileExpenseWrite,
  hasUnsavedExpenseChanges,
} from "./expenses/expenseWriteGuard";
import {
  withSaveTimeout,
  isOffline,
  OFFLINE_MESSAGE,
  TIMEOUT_MESSAGE,
} from "./expenses/saveTimeout";
import {
  resolveIncomeLoad,
  hasUnsavedIncomeChanges,
} from "./income/incomeWriteGuard";
import {
  resolveRiskLoad,
  guardRiskWrite,
  isCompletedAssessment,
} from "./risk/riskWriteGuard";
import {
  resolveDebtLoad,
  guardDebtWrite,
  hasUnsavedDebtChanges,
} from "./debt/debtWriteGuard";
import {
  resolveInvestmentLoad,
  guardInvestmentWrite,
  hasUnsavedInvestmentChanges,
} from "./investments/investmentWriteGuard";
import {
  resolveWalletLoad,
  guardWalletWrite,
  hasUnsavedWalletChanges,
} from "./wallets/walletWriteGuard";

/* ─────────────────────────────────────────────────────────────────────── */

/* ─── Risk Assessment imports ─────────────────────────────────────────── */
import questions from './riskassessment/data/crypto-questions.js';
import { calculateResults } from './riskassessment/utils/cryptoCalculationUtils.js';
import QuestionSlider from './riskassessment/QuestionSlider';
import ProgressBar from './riskassessment/ProgressBar';
import CryptoReportView from './riskassessment/CryptoReportView';
import logo from '../assets/brand/ARC-Ed-Wordmark-Gold.svg';

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];
const nextMonth = (abbr) => MONTHS[(MONTHS.indexOf(abbr) + 1) % 12];

const FinancialDashboard = ({ user, onLogout }) => {
  /* ─── Research feature flags (defaults to all off until Firestore loads) ── */
  const { flags: researchFlags } = useResearchFlags();

  /* ─── Strategy Automation feature flag (defaults OFF until Firestore loads) ── */
  const {
    enabled: strategyAutomationEnabled,
    loading: strategyAutomationLoading,
  } = useStrategyAutomationFlag();

  /* ─── Theme (brand-default dark; toggle lives in the Header) ─────────────── */
  const { theme, toggle: toggleTheme } = useTheme();

  /* ─── UI state ──────────────────────────────────────────────────────── */
  const [activeTab,   setActiveTab]   = useState('overview');

  // Mobile sidebar drawer state. On lg+ the sidebar is permanently
  // visible regardless of this flag (CSS-controlled). On smaller
  // viewports the sidebar is hidden until the hamburger button toggles
  // this on.
  const [sidebarOpen, setSidebarOpen] = useState(false);

  /* ─── Deep-link: cross-tab navigation event listener ──────────────────── */
  // Any child component anywhere in the tree can navigate by dispatching:
  //   window.dispatchEvent(new CustomEvent('arc:navigate', {
  //     detail: { tab: 'research', researchFilter: { riskCharacter: 'Conservative' } }
  //   }));
  // This avoids threading a callback through five layers of props.
  const [pendingResearchFilter, setPendingResearchFilter] = useState(null);
  /* ARC-14: deep-links must clear the same unsaved-changes guard the sidebar
     uses. Held in a ref because the listener effect below has [] deps and would
     otherwise capture the first render's closure - and with it an expensesDirty
     that is permanently false. */
  const requestTabChangeRef = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      const tab = e.detail?.tab;
      if (!tab) return;
      const go = requestTabChangeRef.current;
      const changed = go ? go(tab) : (setActiveTab(tab), true);
      // Member chose to stay: do not move, and do not apply a filter either.
      if (!changed) return;
      if (tab === 'research' && e.detail?.researchFilter) {
        setPendingResearchFilter(e.detail.researchFilter);
      }
    };
    window.addEventListener('arc:navigate', handler);
    return () => window.removeEventListener('arc:navigate', handler);
  }, []);
  const [editMode,    setEditMode]    = useState(false);
  const [chartsReady, setChartsReady] = useState(false);
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [lastSaved,   setLastSaved]   = useState(null);
  const [error,       setError]       = useState('');
  // GUARD (2026-08-03): true only after a successful server load. Saves are
  // blocked until then, so a failed/raced load can never overwrite real data
  // with the sample defaults (the clobber bug).
  const loadedOk = useRef(false);

  /* ARC-14 ------------------------------------------------------------------
     committedExpenses = the expense state the SERVER has confirmed. Three jobs:
       1. the Expenses tab's derived widgets render from it, never from the edit
          buffer, so a figure on screen is never one we failed to persist;
       2. it is the merge base for the read-before-write reconcile below;
       3. buffer !== committed is the unsaved-changes flag for the nav guards.
     Kept in a ref as well because a queued save runs after the render that
     scheduled it. */
  const [committedExpenses, setCommittedExpenses] = useState(null);
  const committedExpensesRef = useRef(null);
  const commitExpenses = (payload) => {
    committedExpensesRef.current = payload;
    setCommittedExpenses(payload);
  };

  /* ARC-58 ------------------------------------------------------------------
     Same shape for income, for jobs (1) and (3) above: the Income tab's stat
     cards and charts render from committedIncome, and buffer !== committed is
     its unsaved-changes flag. No merge base — income has no reconcile step in
     this issue. Null until a load confirms what the server holds; after a
     FAILED load it stays null, because we then know nothing at all. */
  const [committedIncome, setCommittedIncome] = useState(null);
  const commitIncome = (payload) => setCommittedIncome(payload);

  /* Reactive twin of loadedOk, for the income read. loadedOk is a ref, so a
     render cannot see it; the Income tab needs to render a read-only error
     state, which is a render-time decision. */
  const [incomeLoadError, setIncomeLoadError] = useState('');

  /* ARC-59 ------------------------------------------------------------------
     Same shape again for the risk assessment, with one addition income does not
     need: `null` and "server confirmed there is none" are different states here
     (see risk/riskWriteGuard.js), so the snapshot is an OBJECT WRAPPER.
     committedRisk === null            -> we do not know what the server holds.
     committedRisk.riskAssessmentData  -> what it confirmed, possibly null.
     The ref twin is read inside writeUserData, which closes over stale state. */
  const [committedRisk, setCommittedRisk] = useState(null);
  const committedRiskRef = useRef(null);
  const commitRisk = (payload) => {
    committedRiskRef.current = payload;
    setCommittedRisk(payload);
  };

  /* Render-time twin of loadedOk for the risk read, for the same reason as
     incomeLoadError: the Risk Profile tab has to decide whether to mount the
     assessment at all, and a ref cannot drive that. */
  const [riskLoadError, setRiskLoadError] = useState('');

  /* ARC-60 ------------------------------------------------------------------
     Same shape again for debts: the Debt tab's charts and summary render from
     committedDebts, and buffer !== committed is its unsaved-changes flag. Null
     until a load confirms what the server holds; after a FAILED load it stays
     null, because we then know nothing at all. The ref twin is read inside
     writeUserData, which closes over stale state. */
  const [committedDebts, setCommittedDebts] = useState(null);
  const committedDebtsRef = useRef(null);
  const commitDebts = (payload) => {
    committedDebtsRef.current = payload;
    setCommittedDebts(payload);
  };

  /* Render-time twin of loadedOk for the debt read: the Debt tab renders a
     read-only error state, which is a render-time decision a ref cannot drive. */
  const [debtLoadError, setDebtLoadError] = useState('');

  /* ARC-61 ------------------------------------------------------------------
     Same shape for the investment field group (`investments`,
     `investmentHistory`, `investmentCategories` — one committed snapshot,
     because one save writes all three). The Investments tab's donut, growth
     chart, category summary and portfolio total render from it, and buffer !==
     committed is its unsaved-changes flag. */
  const [committedInvestments, setCommittedInvestments] = useState(null);
  const committedInvestmentsRef = useRef(null);
  const commitInvestments = (payload) => {
    committedInvestmentsRef.current = payload;
    setCommittedInvestments(payload);
  };
  const [investmentLoadError, setInvestmentLoadError] = useState('');

  /* ARC-61 ------------------------------------------------------------------
     And for wallets. Here the snapshot carries more weight than anywhere else:
     it is the ONLY way to tell "the member removed their last wallet" from "no
     load ever told us they had one" — both are an empty array. guardWalletWrite
     will not write [] over a non-empty server value without it. */
  const [committedWallets, setCommittedWallets] = useState(null);
  const committedWalletsRef = useRef(null);
  const commitWallets = (payload) => {
    committedWalletsRef.current = payload;
    setCommittedWallets(payload);
  };
  const [walletLoadError, setWalletLoadError] = useState('');

  /* Armed only by the member pressing "Retake Assessment" and confirming, and
     disarmed by the save that carries the retake. Without it the write guard
     cannot tell a deliberate restart from the mount-time wipe that caused this
     issue — both are an empty assessment replacing a completed one. */
  const riskRetakeAckRef = useRef(false);

  /* Saves run one at a time, in order. Previously a save that arrived while
     another was in flight hit `if (saving) return` and was DROPPED with no
     error and no retry - the "it said it saved but nothing was written" half of
     ARC-14. Chaining queues it instead. */
  const saveChainRef = useRef(Promise.resolve());

  /* ─── Data state ────────────────────────────────────────────────────── */
  const [incomeData,    setIncomeData]    = useState([]);
  const [expenseData,   setExpenseData]   = useState([]);
  const [goals,         setGoals]         = useState([]);
  const [debts,         setDebts]         = useState([]);
  const [netWorth,      setNetWorth]      = useState({});
  const [emergencyFund, setEmergencyFund] = useState({});
  const [investments,   setInvestments]   = useState([]);
  const [dcaData, setDcaData] = useState({ monthlyAmount: 0, autoDcaEnabled: true, history: [] });
  const [userAge, setUserAge] = useState(30);
  const [dob, setDob] = useState("");
  const [wallets, setWallets] = useState([]);
  const [retirementSettings, setRetirementSettings] = useState({
    retireAge: 65,
    growthRate: 0.07,
    autoEstimate: false,
    timeRange: "all",
    contributionMode: "simulated",
    simulatedMonthly: "",
    drawdownYears: 30,
    drawdownReturn: 0.05,
  });


  /* ─── Column configuration state ─────────────────────────────────────── */
  const [incomeColumns, setIncomeColumns] = useState(['salary', 'business', 'rental', 'crypto', 'affiliate']);
  const [expenseColumns, setExpenseColumns] = useState(['rent', 'utilities', 'insurance', 'groceries', 'entertainment', 'travel', 'other']);

  /* ─── Investment enhancement state ───────────────────────────────────── */
  const [investmentCategories, setInvestmentCategories] = useState(['Emergency Fund', 'Retirement', 'Growth', 'Income', 'Speculative', 'Real Estate', 'Crypto']);
  const [investmentHistory, setInvestmentHistory] = useState({});

  /* ─── Risk Assessment state ──────────────────────────────────────────── */
const [riskAssessmentData, setRiskAssessmentData] = useState(null);

  /* ─── default data (for fresh accounts or load failure) ─────────────── */
  const getDefaultData = () => ({
    /* ARC-58: the `incomeData` demo ladder that used to live here is gone. It
       was the payload of the 2026-08-18 overwrite: a failed or incomplete read
       seeded it into the income buffer and autosave wrote twelve invented
       months over the member's real figures. Income now only ever comes from
       the server (see income/incomeWriteGuard.js), so there is no default left
       for a future edit to reach for. */
    expenseData: [
      { month: 'Oct', rent: 3500, utilities: 100, insurance: 100, groceries: 100, entertainment: 200, travel: 300, other:  300, total:  4600 },
      { month: 'Nov', rent:    0, utilities:   0, insurance:   0, groceries:   0, entertainment:   0, travel:   0, other:10000, total: 10000 },
      { month: 'Dec', rent:    0, utilities:   0, insurance:   0, groceries:   0, entertainment:   0, travel:   0, other:100000,total:100000 },
    ],
    goals: [
      { name: 'FIRE Target',       target: 2000000, current: 1000000, progress: 50 },
      { name: 'Savings Target',    target:   50000, current:       0, progress:  0 },
      { name: 'Investment Target', target:  100000, current:       0, progress:  0 },
    ],
    /* ARC-60: the `debts` scaffold that used to live here is gone. It was the
       payload of the 2026-08-18 overwrite: three separate load branches seeded
       "Credit Card A" / "Student Loan B" / "Car Loan C" / "Mortgage D" into the
       debt buffer, two of them with saving ARMED, and autosave wrote four
       invented debts over the member's real ones. Debts now only ever come from
       the server (see debt/debtWriteGuard.js), so there is no default left for
       a future edit to reach for. */
    netWorth:      { assets: 300000, liabilities: 5250, netWorth: 294750, fireGoal: 2000000, progress: 14.74 },
    emergencyFund: { monthlyExpenses: 5500, threeMonthGoal: 16500, sixMonthGoal: 33000, current: 0, progress: 0 },
    /* ARC-61: the `investments` scaffold and the `investmentHistory` ladder
       that used to live here are gone. They were seeded by three load branches,
       two of them with saving ARMED, putting nine invented holdings worth
       $325,000 and a full year of invented monthly totals into the same state
       autosave serialises. Worse, the Investments tab's syncCurrentTotal effect
       then stamped the scaffold's total into the history as a figure that looks
       exactly like one the member entered. Both fields now only ever come from
       the server (see investments/investmentWriteGuard.js), so there is no
       default left for a future edit to reach for. */
    riskAssessmentData: null

  });

  /* ─── Column update handlers ─────────────────────────────────────────── */
  const updateExpenseColumns = (newColumns) => {
    console.log('Parent: Updating expense columns to:', newColumns);
    
    // 1. Update column configuration
    setExpenseColumns(newColumns);
    
    // 2. Update all expense data rows to match new columns
    setExpenseData(currentExpenseData => {
      return currentExpenseData.map(row => {
        const newRow = { month: row.month };
        // ARC-14: line items are per-column drill-downs; rebuilding the row from
        // scratch used to drop every one of them on any column edit.
        if (row.items) newRow.items = row.items;
        
        // Add all columns from newColumns, keeping existing values or defaulting to 0
        newColumns.forEach(col => {
          newRow[col] = row[col] || 0;
        });
        
        // Recalculate total
        newRow.total = newColumns.reduce((sum, col) => sum + (newRow[col] || 0), 0);
        
        return newRow;
      });
    });
  };

  const updateIncomeColumns = (newColumns) => {
    console.log('Parent: Updating income columns to:', newColumns);
    
    // 1. Update column configuration
    setIncomeColumns(newColumns);
    
    // 2. Update all income data rows to match new columns
    setIncomeData(currentIncomeData => {
      return currentIncomeData.map(row => {
        const newRow = { month: row.month };
        
        // Add all columns from newColumns, keeping existing values or defaulting to 0
        newColumns.forEach(col => {
          newRow[col] = row[col] || 0;
        });
        
        // Recalculate total
        newRow.total = newColumns.reduce((sum, col) => sum + (newRow[col] || 0), 0);
        
        return newRow;
      });
    });
  };

  /* ─── Investment enhancement handlers ────────────────────────────────── */
  const updateInvestmentCategories = (newCategories) => {
    console.log('Parent: Updating investment categories to:', newCategories);
    setInvestmentCategories(newCategories);
  };

  const updateInvestmentHistory = (year, month, value) => {
    console.log('Parent: Updating investment history:', year, month, value);
    setInvestmentHistory(prev => ({
      ...prev,
      [year]: {
        ...prev[year],
        [month]: value
      }
    }));
  };

  /* ─── add-row helpers ───────────────────────────────────────────────── */
  /* ARC-63: this used to open with
       if (!incomeData.length || !expenseData.length) return;
     and then derive the month from incomeData's last row. Since ARC-58 a
     brand-new account starts with an EMPTY income table while expenses are
     still seeded from getDefaultData, so that guard short-circuited on every
     new account and "Add Month" silently did nothing - on NetIncome and
     Expenses, whose only way to add a month is this button.

     The sequence now comes from whichever table has rows, and from the current
     calendar month when neither does. The two arrays are read POSITIONALLY
     elsewhere - netIncomeData pairs incomeData[i] with expenseData[i], and the
     shared deleteMonth(index) filters both by the same index - so the shorter
     table is first padded with zero rows for the months the longer one already
     has. Appending to both without that padding would leave them off by the
     difference and pair January income against October expenses. The padded
     rows are zeros for months already on screen, not figures, and they are only
     ever created by the member's own click on Add Month. */
  const addNextMonth = () => {
    // The longer table defines the month sequence; on a brand-new account that
    // is expenses, which is exactly the case the old guard rejected.
    const base = incomeData.length >= expenseData.length ? incomeData : expenseData;

    const next = base.length
      ? nextMonth(base[base.length - 1].month)
      : MONTHS[new Date().getMonth()];

    const makeRow = (month, cols) => {
      const row = { month, total: 0 };
      cols.forEach(col => (row[col] = 0));
      return row;
    };

    // Bring `rows` up to the base sequence, then append the new month.
    const extend = (rows, cols) => [
      ...rows,
      ...base.slice(rows.length).map(r => makeRow(r.month, cols)),
      makeRow(next, cols),
    ];

    setIncomeData(prev => extend(prev, incomeColumns));
    setExpenseData(prev => extend(prev, expenseColumns));
  };


  const deleteMonth = (index) => {
    if (!window.confirm("Are you sure you want to delete this month?")) return;

    setIncomeData(prev => prev.filter((_, i) => i !== index));
    setExpenseData(prev => prev.filter((_, i) => i !== index));
  };


 const addDebt = () => {
    setEditMode(true);                                   
    setDebts((d) => [
      ...d,
      { name: 'New Debt', original: 0, current: 0, paidOff: 0, apr: 0, monthlyPayment: 0, totalInterestPaid: 0},
    ]);
  };
  
  const removeDebt = (index) => {
    if (!window.confirm("Are you sure you want to delete this debt?")) return;
    setDebts((d) => d.filter((_, i) => i !== index));
  };

  const addInvestment = (newInvestment) => {
    const investment = newInvestment || { name: 'New Investment', type: 'Emergency Fund', amount: 0 };
    setInvestments((d) => [...d, investment]);
  };

  const removeInvestment = (index) => {
    setInvestments((d) => d.filter((_, i) => i !== index));
  };

  /* ─── data loading (runs on user change) ─────────────────────────────── */
  useEffect(() => {
    const load = async () => {
      if (!user) return;
      setLoading(true);
      setError('');
      /* ARC-58: the ONLY way income state is set during a load. Every branch
         goes through resolveIncomeLoad, which has no code path that returns
         demo rows - so the scaffold that was written over real member income on
         2026-08-18 can no longer reach this buffer, and from there the save. */
      const applyIncome = (result) => {
        setIncomeColumns(result.incomeColumns);
        setIncomeData(result.incomeData);
        commitIncome(result.committed);
        setIncomeLoadError(result.error);
      };
      /* ARC-59: the ONLY way risk assessment state is set during a load. Before
         this, two of the branches below simply never mentioned
         riskAssessmentData, so it kept its `null` initial value and the tab
         built a blank assessment on top of it. Every branch now states what the
         read means, and "the read told us nothing" is no longer spelled the
         same way as "the member has not taken it". */
      const applyRisk = (result) => {
        setRiskAssessmentData(result.riskAssessmentData);
        commitRisk(result.committed);
        setRiskLoadError(result.error);
      };
      /* ARC-60: the ONLY way debt state is set during a load. Every branch goes
         through resolveDebtLoad, which has no code path that returns a
         placeholder debt - so the four demo rows that were written over real
         member debts on 2026-08-18 can no longer reach this buffer, and from
         there the save. */
      const applyDebts = (result) => {
        setDebts(result.debts);
        commitDebts(result.committed);
        setDebtLoadError(result.error);
      };
      /* ARC-61: the ONLY way investment state is set during a load. All three
         fields move together because one save writes all three, and
         resolveInvestmentLoad has no code path that returns a placeholder
         holding or a placeholder month. */
      const applyInvestments = (result) => {
        setInvestments(result.investments);
        setInvestmentHistory(result.investmentHistory);
        setInvestmentCategories(result.investmentCategories);
        commitInvestments(result.committed);
        setInvestmentLoadError(result.error);
      };
      /* ARC-61: and for wallets - including the two branches that previously
         said nothing about the field at all and left the buffer at its empty
         initial value with saving armed. */
      const applyWallets = (result) => {
        setWallets(result.wallets);
        commitWallets(result.committed);
        setWalletLoadError(result.error);
      };
      /* ARC-58: the closed state, reached by BOTH ways a read can fail.
         authService.loadFinancialData() catches its own Firestore errors and
         RESOLVES `{ success: false, error }` - it never rejects - so a catch
         block alone leaves this path unreachable in the real app. Without the
         `!res.success` branch below, a failed read fell through to the
         brand-new-account case: saving enabled, and an empty income table
         sitting in the buffer ready to be autosaved over the member's real
         months. That is the same overwrite as 2026-08-18 with a different
         payload. Both entry points now land here. */
      const failClosed = () => {
        loadedOk.current = false; // LOAD FAILED - never save this state
        setError('Could not load your saved data. Editing is disabled to protect it - please refresh the page.');
        const def = getDefaultData();
        /* Income fails closed: an empty read-only table with an error state, and
           committedIncome stays null - a failed read tells us nothing about what
           the server holds, so we neither show figures as saved nor allow
           anything to be written on top of them. */
        applyIncome(resolveIncomeLoad({ ok: false }));
        /* ARC-59: same closed state for the assessment. committedRisk stays
           null, so the write guard knows it cannot vouch for anything, and the
           tab renders an error instead of mounting a blank assessment - a blank
           one on screen is a blank one in the save path. */
        applyRisk(resolveRiskLoad({ ok: false }));
        /* ARC-60: debts fail closed too - an empty read-only list with an error
           state, and committedDebts stays null. This branch used to seed the
           placeholder scaffold; loadedOk blocked the write, but the demo rows
           were sitting in the save path regardless, one missed guard away from
           being written. */
        applyDebts(resolveDebtLoad({ ok: false }));
        /* ARC-61: investments fail closed - an empty read-only portfolio with
           an error state, and committedInvestments stays null. This branch used
           to seed the nine-holding scaffold and a year of invented history;
           loadedOk blocked the write, but they were sitting in the save path
           regardless, one missed guard away from being written. */
        applyInvestments(resolveInvestmentLoad({ ok: false }));
        /* ARC-61: and wallets, which this branch never mentioned at all. */
        applyWallets(resolveWalletLoad({ ok: false }));
        setExpenseData(def.expenseData);
        setGoals(def.goals);
        setNetWorth(def.netWorth);
        setEmergencyFund(def.emergencyFund);
        // Nothing is confirmed after a failed load, but the widgets must match
        // what is on screen. Saving stays blocked by loadedOk either way.
        commitExpenses({ expenseColumns, expenseData: def.expenseData });
        // Keep default columns and categories as set above
      };
      try {
        const res = await authService.loadFinancialData();
        if (!res?.success) {
          // The service reported failure instead of throwing. Same closed state.
          console.error('Error loading financial data:', res?.error);
          failClosed();
        } else if (res.data && Object.keys(res.data).length) {
          const d = res.data;
          
          // Load column configurations
          const loadedExpenseColumns = d.expenseColumns || ['rent', 'utilities', 'insurance', 'groceries', 'entertainment', 'travel', 'other'];
          const loadedExpenseData    = d.expenseData ?? getDefaultData().expenseData;
          applyIncome(resolveIncomeLoad({ ok: true, data: d }));
          setExpenseColumns(loadedExpenseColumns);
          
          /* ARC-61: was three separate assignments, one of which
             (`d.investments ?? getDefaultData().investments`) defaulted a
             document with no `investments` field to the nine-holding scaffold
             with saving already armed. resolveInvestmentLoad defaults to an
             empty portfolio instead, and fails the read closed on a shape it
             cannot parse rather than coercing it away. */
          applyInvestments(resolveInvestmentLoad({ ok: true, data: d }));

          // Load data (income is handled by applyIncome above)
          setExpenseData(  loadedExpenseData);
          // ARC-14: this is what the server has; the Expenses widgets read it.
          commitExpenses({ expenseColumns: loadedExpenseColumns, expenseData: loadedExpenseData });
          setGoals(        d.goals         ?? getDefaultData().goals);
          /* ARC-60: was `setDebts(d.debts ?? getDefaultData().debts)`, then
             OVERWRITTEN thirty lines below by a second setDebts that ran the
             same fallback through a migration map. Both defaulted a document
             with no `debts` field to the placeholder scaffold, with saving
             already armed on this branch. resolveDebtLoad does the migration
             and defaults to an empty list instead. */
          applyDebts(resolveDebtLoad({ ok: true, data: d }));
          setNetWorth(     d.netWorth      ?? getDefaultData().netWorth);
          setEmergencyFund(d.emergencyFund ?? getDefaultData().emergencyFund);
          /* ARC-59: was `setRiskAssessmentData(d.riskAssessmentData ?? null)`.
             That accepted any shape: a stored value that was not a plain object
             became a blank assessment on screen via the component's optional
             chaining, and autosave wrote the blank over it. resolveRiskLoad
             fails closed on that instead. */
          applyRisk(resolveRiskLoad({ ok: true, data: d }));
          /* ARC-61: was `setWallets(d.wallets ?? [])`. `??` catches null and
             undefined only, so a stored value of any other unreadable shape
             went straight to a tab that maps over it - and, once coerced away
             by the render, straight back to the server as []. resolveWalletLoad
             fails the read closed on that instead, and records the committed
             snapshot that lets the write guard tell a deliberate deletion from
             a load that never happened. */
          applyWallets(resolveWalletLoad({ ok: true, data: d }));
          if (d.dcaData) {
            setDcaData({
              monthlyAmount: 0,
              autoDcaEnabled: true,
              history: [],
              autoPercent: 20,
              ...d.dcaData,
            });
          }
          if (d.dob) setDob(d.dob);
          if (d.userAge) setUserAge(d.userAge);
          if (d.retirementSettings) {
            setRetirementSettings((prev) => ({ ...prev, ...d.retirementSettings }));
          }

          /* ARC-60: the apr/monthlyPayment/totalInterestPaid migration that
             used to run here now lives in resolveDebtLoad's normalizeDebt,
             called by the applyDebts above - one decision point instead of two
             setDebts calls racing to describe the same field. */

          if (d.updatedAt?.seconds) setLastSaved(new Date(d.updatedAt.seconds * 1000));
          loadedOk.current = true; // real data loaded - saving is safe
        } else {
          /* Read succeeded and returned nothing to load. In practice a missing
             document comes back from AuthService as its default structure, which
             takes the branch above; this is the empty-payload case. Either way
             the read reached the server, so saving is safe. */
          loadedOk.current = true;
          const def = getDefaultData();
          /* ARC-58: a new account starts with an EMPTY income table, not the
             demo ladder. Seeding it meant the member's first autosave wrote
             twelve invented months of income to their document as if they had
             entered them. Months are added from the tab's own "Add Month". */
          applyIncome(resolveIncomeLoad({ ok: true, data: null }));
          /* ARC-59: THE 2026-08-18 BRANCH. This one sets loadedOk true, so
             saving is on, and it used to leave riskAssessmentData at its `null`
             initial value - the tab then mounted, mirrored a blank assessment
             back into the parent, and autosave wrote `{answers:{}, results:null}`
             over the member's completed one. (The same silence about userAge is
             why the overwritten document carried the default 30.)

             The read did reach the server, so an absent assessment is a real
             answer and stays saveable - but it is now a CONFIRMED absence, and
             the write guard can tell that apart from an assumed one. */
          applyRisk(resolveRiskLoad({ ok: true, data: null }));
          /* ARC-60: THE 2026-08-18 BRANCH for debts. It set loadedOk true - so
             autosave was armed - and then seeded the four placeholder rows into
             the buffer. Two seconds later they were written over the member's
             real debts, with no user action beyond opening the app. A member
             who has saved nothing starts with an EMPTY debt list; rows come
             from the tab's own "Add Debt". */
          applyDebts(resolveDebtLoad({ ok: true, data: null }));
          /* ARC-61: THE ARMED BRANCH for investments. loadedOk is true here, so
             autosave is on, and this used to seed nine placeholder holdings and
             a year of invented monthly totals into the buffer. A member who has
             saved nothing starts with an EMPTY portfolio; holdings come from
             the tab's own "Add Investment". */
          applyInvestments(resolveInvestmentLoad({ ok: true, data: null }));
          /* ARC-61: THE ARMED BRANCH for wallets, by SILENCE. It never assigned
             the field, so the buffer stayed at its empty initial value with
             saving on - and two seconds later that empty array replaced the
             member's connected wallets. It is stated explicitly now, and the
             committed snapshot records that this absence was CONFIRMED by a
             read rather than assumed. */
          applyWallets(resolveWalletLoad({ ok: true, data: null }));
          setExpenseData(def.expenseData);
          setGoals(def.goals);
          setNetWorth(def.netWorth);
          setEmergencyFund(def.emergencyFund);
          commitExpenses({ expenseColumns, expenseData: def.expenseData });
          // Keep default columns and categories as set above
        }
      } catch (e) {
        // Defensive: the service is not expected to reject, but if it ever does
        // the outcome must be identical to the reported-failure branch above.
        console.error(e);
        failClosed();
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  /* ─── save to Firebase (throttled autosave) ────────────────────────────
     ARC-14: every save now (a) waits its turn instead of being dropped,
     (b) reconciles the expense payload against the server's CURRENT state
     before writing, and (c) resolves to an explicit { success, error } the
     caller can show. It never resolves successfully without a server write. */
  const writeUserData = async () => {
    if (!user) return { success: false, error: 'You are not signed in.' };
    if (!loadedOk.current) {
      const msg = 'Saving is disabled because your data did not load. Refresh the page first.';
      setError(msg);
      return { success: false, error: msg };
    }
    /* ARC-58: income specifically failed to load. loadedOk already blocks this
       write, but the income gate is stated here too, so that a future change to
       the global one cannot silently re-open the path that put an empty or
       default income table on top of real server data. */
    if (incomeLoadError) {
      setError(incomeLoadError);
      return { success: false, error: incomeLoadError };
    }
    /* ARC-60: the same gate for debts, stated here for the same reason - so a
       future change to the global loadedOk check cannot silently re-open the
       path that put placeholder debts on top of real server data. Acceptance
       criterion 1: a failed read issues ZERO writes. */
    if (debtLoadError) {
      setError(debtLoadError);
      return { success: false, error: debtLoadError };
    }
    /* ARC-61: the same gates for the investment field group and for wallets,
       stated here for the same reason - so a future change to the global
       loadedOk check cannot silently re-open the paths that put a placeholder
       portfolio, or an empty wallet list, on top of real server data.
       Acceptance criterion 1: a failed read issues ZERO writes. */
    if (investmentLoadError) {
      setError(investmentLoadError);
      return { success: false, error: investmentLoadError };
    }
    if (walletLoadError) {
      setError(walletLoadError);
      return { success: false, error: walletLoadError };
    }
    if (!authService.isAuthenticated()) {
      const msg = 'User authentication expired. Please log in again.';
      setError(msg);
      return { success: false, error: msg };
    }
    /* ARC-14 follow-up: refuse offline instead of queueing. Firestore would
       accept this write into its local queue and flush it whenever the network
       returned - a snapshot from minutes ago landing on top of whatever the
       server holds by then, which is the exact clobber this issue exists to
       prevent. Nothing is queued, and nothing is claimed. */
    if (isOffline()) {
      setError(OFFLINE_MESSAGE);
      return { success: false, error: OFFLINE_MESSAGE };
    }
    setSaving(true);
    setError('');
    try {
      /* ROOT CAUSE (ARC-14): saveFinancialData lands as updateDoc(doc, {...data}),
         and expenseColumns / expenseData are top-level fields - so each write
         REPLACES them wholesale from this client's snapshot. A session holding a
         snapshot older than another session's column additions wrote its array
         back and deleted them, taking the rows' values with it. Read what is on
         the server immediately before writing and three-way merge against it. */
      let expensePayload = { expenseColumns, expenseData };
      let current;
      try {
        /* getDoc resolves from the local cache when offline, so this read can
           "succeed" without ever reaching the server. The timeout bounds the
           case where it neither resolves nor rejects. */
        current = await withSaveTimeout(authService.loadFinancialData());
      } catch (e) {
        console.error(e);
        current = { success: false, error: e?.message };
      }
      if (!current?.success) {
        // Writing blind here is exactly what deleted the columns. Refuse, and say so.
        const msg = 'Could not check your saved data, so nothing was written. Check your connection and save again.';
        setError(msg);
        return { success: false, error: msg };
      }
      // A document with no updatedAt is the not-yet-created placeholder
      // loadFinancialData() returns - there is nothing on the server to protect.
      const serverDoc = current.data?.updatedAt ? current.data : null;

      /* ARC-59: the last gate before the assessment goes into the write.
         `riskAssessmentData` is a top-level field, so like the expense arrays it
         is REPLACED wholesale by whatever this client sends - and unlike them it
         had no reconcile step at all, which is how a blank assessment assembled
         on mount reached the document.

         Two ways the client buffer is not allowed to be the source here:
         (a) the read that produced it failed or could not be parsed, so it was
             never the server's value to begin with; or
         (b) it is empty and the server's copy is not.
         Both resolve to "write the server's own value back", which leaves the
         field untouched rather than blocking the member's other tabs. */
      const riskPayload = guardRiskWrite({
        outgoing: riskAssessmentData,
        outgoingTrusted: !riskLoadError,
        committed: committedRiskRef.current,
        server: serverDoc?.riskAssessmentData,
        /* A failed risk read can never be a retake: the member cannot have
           pressed a button on a tab that refused to render. */
        retakeAcknowledged: !riskLoadError && riskRetakeAckRef.current,
      });
      if (riskPayload.blocked) {
        console.warn('[ARC-59] blocked an empty assessment from replacing a completed one');
        /* Adopt what is actually being stored, so the tab cannot keep
           re-proposing the blank one on every autosave tick. */
        setRiskAssessmentData(riskPayload.riskAssessmentData);
        setError(riskPayload.warning);
      }

      /* ARC-60: the last gate before `debts` goes into the write. Like
         `riskAssessmentData` it is a top-level field replaced wholesale by
         whatever this client sends, and it had no reconcile step at all - which
         is how four placeholder rows assembled by the load effect reached the
         document. The debtLoadError gate above already refuses an untrusted
         save outright; this is the defence in depth for the day someone edits
         that gate out. */
      const debtPayload = guardDebtWrite({
        outgoing: debts,
        outgoingTrusted: !debtLoadError,
        committed: committedDebtsRef.current,
        server: serverDoc?.debts,
      });
      if (debtPayload.blocked) {
        console.warn('[ARC-60] blocked placeholder debts from replacing saved ones');
        // Adopt what is actually being stored, so the buffer cannot keep
        // re-proposing the scaffold on every autosave tick.
        setDebts(debtPayload.debts);
        setError(debtPayload.warning);
      }

      /* ARC-61: the last gate before the investment fields go into the write.
         Three top-level fields, all replaced wholesale by whatever this client
         sends, and none of them had a reconcile step. The investmentLoadError
         gate above already refuses an untrusted save outright; this is the
         defence in depth for the day someone edits that gate out. */
      const investmentPayload = guardInvestmentWrite({
        outgoing: investments,
        outgoingHistory: investmentHistory,
        outgoingCategories: investmentCategories,
        outgoingTrusted: !investmentLoadError,
        committed: committedInvestmentsRef.current,
        server: serverDoc,
      });
      if (investmentPayload.blocked) {
        console.warn('[ARC-61] blocked placeholder investments from replacing saved ones');
        // Adopt what is actually being stored, so the buffer cannot keep
        // re-proposing the scaffold on every autosave tick.
        setInvestments(investmentPayload.investments);
        setInvestmentHistory(investmentPayload.investmentHistory);
        setError(investmentPayload.warning);
      }

      /* ARC-61: and for wallets. The rule this one enforces is narrower than
         the others and is the whole reason the wallet guard exists: an EMPTY
         outgoing list may not replace a non-empty server one until some load
         has confirmed what the server holds. After that it may - removing your
         last wallet is a real thing members do. */
      const walletPayload = guardWalletWrite({
        outgoing: wallets,
        outgoingTrusted: !walletLoadError,
        committed: committedWalletsRef.current,
        server: serverDoc?.wallets,
      });
      if (walletPayload.blocked) {
        console.warn('[ARC-61] blocked an unconfirmed empty wallet list from replacing saved wallets');
        setWallets(walletPayload.wallets);
        setError(walletPayload.warning);
      }

      const merged = reconcileExpenseWrite({
        base: committedExpensesRef.current,
        client: { expenseColumns, expenseData },
        server: serverDoc && {
          expenseColumns: serverDoc.expenseColumns,
          expenseData: serverDoc.expenseData,
        },
      });
      expensePayload = {
        expenseColumns: merged.expenseColumns,
        expenseData: merged.expenseData,
      };
      if (merged.reconciled) {
        // Another session had written since our snapshot. Adopt the merged
        // result locally so the member sees exactly what we are about to store.
        console.warn('[ARC-14] merged concurrent expense edits', {
          recoveredColumns: merged.recoveredColumns,
          recoveredMonths: merged.recoveredMonths,
          serverPreferredRows: merged.serverPreferredRows,
        });
        setExpenseColumns(expensePayload.expenseColumns);
        setExpenseData(expensePayload.expenseData);
      }

      /* The write promise resolves only on server ack; offline it never settles
         at all. Bound it so the member gets an answer either way. */
      const res = await withSaveTimeout(authService.saveFinancialData({
        // Add column configurations
        incomeColumns,
        // ARC-61: the guarded investment values, never the raw buffers.
        investmentCategories: investmentPayload.investmentCategories,
        investmentHistory: investmentPayload.investmentHistory,
        // Existing data
        incomeData,
        goals,
        // ARC-60: the guarded value, never the raw buffer.
        debts: debtPayload.debts,
        netWorth,
        emergencyFund,
        // ARC-61: the guarded value, never the raw buffer.
        investments: investmentPayload.investments,
        // ARC-59: the guarded value, never the raw buffer.
        riskAssessmentData: riskPayload.riskAssessmentData,
        dcaData,
        userAge,
        dob,
        // ARC-61: the guarded value, never the raw buffer.
        wallets: walletPayload.wallets,
        retirementSettings,
        // Reconciled expense payload (never the raw snapshot)
        ...expensePayload,
      }));
      if (res.success) {
        setLastSaved(new Date());
        commitExpenses(expensePayload);
        // ARC-58: the server has now confirmed this income; the tab's widgets
        // may show it.
        commitIncome({ incomeColumns, incomeData });
        /* ARC-60: the server has confirmed these debts; the tab's charts and
           summary may show them. Not after an untrusted read - that write only
           echoed the server's own value back, and we never understood it well
           enough to vouch for it. committedDebts stays null: still unknown,
           which is the truth. */
        if (!debtLoadError) commitDebts({ debts: debtPayload.debts });
        /* ARC-61: the server has confirmed this portfolio; the tab's donut,
           growth chart, category summary and total may show it. Not after an
           untrusted read - that write only echoed the server's own values back,
           and we never understood them well enough to vouch for them. */
        if (!investmentLoadError) {
          commitInvestments({
            investments: investmentPayload.investments,
            investmentHistory: investmentPayload.investmentHistory,
            investmentCategories: investmentPayload.investmentCategories,
          });
        }
        /* ARC-61: same for wallets. Committing after an untrusted read would be
           worse here than elsewhere - a committed snapshot is precisely what
           licenses a later empty list to be written, so vouching for a value we
           could not read would hand that licence to the next autosave tick. */
        if (!walletLoadError) commitWallets({ wallets: walletPayload.wallets });
        /* ARC-59: the server has confirmed this assessment, so the snapshot the
           guard compares against is now this write - including a retake, which
           is the one time an empty assessment legitimately becomes the truth.

           Not after an untrusted read: that write only echoed the server's own
           value back, and we never understood it well enough to vouch for it.
           Committing it would let a later blank assessment past the guard.
           committedRisk stays null - still unknown, which is the truth. */
        if (!riskLoadError) {
          commitRisk({ riskAssessmentData: riskPayload.riskAssessmentData });
          // The retake has landed; disarm so the next blank one is caught again.
          riskRetakeAckRef.current = false;
        }
        return { success: true };
      }
      const msg = 'Failed to save data. Your changes are still on screen - try saving again.';
      setError(msg);
      return { success: false, error: res.error || msg };
    } catch (e) {
      console.error(e);
      // Keep the specific timeout wording; the generic message would hide it.
      const msg =
        e?.message === TIMEOUT_MESSAGE
          ? TIMEOUT_MESSAGE
          : 'Failed to save your data. Your changes are still on screen - try saving again.';
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setSaving(false);
    }
  };

  /* Serialize saves: queue behind whatever is running instead of dropping. */
  const saveUserData = () => {
    const next = saveChainRef.current.catch(() => {}).then(() => writeUserData());
    saveChainRef.current = next;
    return next;
  };

  /* autosave after 2 s idle */
  useEffect(() => {
    if (loading || !loadedOk.current) return;
    const t = setTimeout(() => { saveUserData(); }, 2000);
    return () => clearTimeout(t);
  }, [incomeColumns, expenseColumns, investmentCategories, investmentHistory, incomeData, expenseData, goals, debts, netWorth, emergencyFund, investments, riskAssessmentData, dcaData, userAge, dob, wallets, retirementSettings]);

  /* ─── ARC-14: unsaved-changes state + in-app navigation guard ───────────
     The browser-unload half lives in the Expenses tab (useUnsavedChangesGuard);
     tab changes can only be caught here, where navigation lives. */
  const expensesDirty = hasUnsavedExpenseChanges(
    { expenseColumns, expenseData },
    committedExpenses
  );

  /* ARC-58: same signal for income. Drives the tab's "these figures are the
     saved ones" marker; the tab-change and logout guards stay expenses-only,
     which is where ARC-14 put them. */
  const incomeDirty = hasUnsavedIncomeChanges(
    { incomeColumns, incomeData },
    committedIncome
  );

  /* ARC-60: same signal for debts. Drives the tab's "these figures are the
     saved ones" marker; the tab-change and logout guards stay expenses-only,
     which is where ARC-14 put them. */
  const debtsDirty = hasUnsavedDebtChanges({ debts }, committedDebts);

  /* ARC-61: same signal for the investment group and for wallets. Drives each
     tab's "these figures are the saved ones" marker; the tab-change and logout
     guards stay expenses-only, which is where ARC-14 put them. */
  const investmentsDirty = hasUnsavedInvestmentChanges(
    { investments, investmentHistory, investmentCategories },
    committedInvestments
  );
  const walletsDirty = hasUnsavedWalletChanges({ wallets }, committedWallets);

  /** Returns whether the navigation actually happened. */
  const requestTabChange = (nextTab) => {
    if (
      nextTab !== activeTab &&
      expensesDirty &&
      !window.confirm(
        'Your expense changes have not been saved yet. Leave this tab and they stay unsaved. Continue?'
      )
    ) {
      return false;
    }
    setActiveTab(nextTab);
    return true;
  };
  // Published for the 'arc:navigate' listener above (see the ref's note).
  requestTabChangeRef.current = requestTabChange;

  /* Logging out leaves the app entirely, so it needs the same guard - otherwise
     unsaved edits disappear with no warning at all. */
  const requestLogout = () => {
    if (
      expensesDirty &&
      !window.confirm(
        'Your expense changes have not been saved yet. Log out and they stay unsaved. Continue?'
      )
    ) {
      return;
    }
    onLogout();
  };

  /* allow Recharts to mount after first paint */
  useEffect(() => {
    const t = setTimeout(() => setChartsReady(true), 100);
    return () => clearTimeout(t);
  }, []);

  /* ─── derive & recalculate totals ------------------------------------- */
  useEffect(() => {
    if (loading) return;

    /* Clean up deleted investments */
    setInvestments(currentInvestments => 
      currentInvestments.filter(inv => !inv.name.includes('[DELETED]'))
    );

    /* income totals - use dynamic columns */
    const newIncome = incomeData.map((r) => ({
      ...r,
      total: incomeColumns.reduce((sum, col) => sum + (r[col] || 0), 0),
    }));
    if (JSON.stringify(newIncome) !== JSON.stringify(incomeData)) setIncomeData(newIncome);

    /* expense totals - use dynamic columns */
    const newExpense = expenseData.map((r) => ({
      ...r,
      total: expenseColumns.reduce((sum, col) => sum + (r[col] || 0), 0),
    }));
    if (JSON.stringify(newExpense) !== JSON.stringify(expenseData)) setExpenseData(newExpense);

    /* goal progress */
    const newGoals = goals.map((g) => ({
      ...g,
      progress: g.target ? (g.current / g.target) * 100 : 0,
    }));
    if (JSON.stringify(newGoals) !== JSON.stringify(goals)) setGoals(newGoals);

    /* debt progress */
    const newDebts = debts.map((d) => {
      const paidOffPercentage = d.original ? ((d.original - d.current) / d.original) * 100 : 0;
      
      // Calculate monthly interest if APR is provided
      const monthlyInterestRate = (d.apr || 0) / 100 / 12;
      const monthlyInterest = d.current * monthlyInterestRate;
      
      return {
          ...d,
          paidOff: paidOffPercentage,
          monthlyInterest: monthlyInterest  // This could be useful for other components
        };
    });
    
    if (JSON.stringify(newDebts) !== JSON.stringify(debts)) setDebts(newDebts);


    /* net-worth metrics */
    const debtSum = newDebts.reduce((s, d) => s + d.current, 0);
    const nw = {
      ...netWorth,
      liabilities: debtSum,
      netWorth: netWorth.assets - debtSum,
      progress: netWorth.fireGoal ? ((netWorth.assets - debtSum) / netWorth.fireGoal) * 100 : 0,
    };
    if (JSON.stringify(nw) !== JSON.stringify(netWorth)) setNetWorth(nw);

    /* emergency-fund progress */
    const ef = {
      ...emergencyFund,
      threeMonthGoal: emergencyFund.monthlyExpenses * 3,
      sixMonthGoal:   emergencyFund.monthlyExpenses * 6,
      progress: emergencyFund.monthlyExpenses
        ? (emergencyFund.current / (emergencyFund.monthlyExpenses * 6)) * 100
        : 0,
    };
    if (JSON.stringify(ef) !== JSON.stringify(emergencyFund)) setEmergencyFund(ef);
  }, [incomeData, expenseData, goals, debts, netWorth, emergencyFund, loading, incomeColumns, expenseColumns]);


  const getDebtSummary = () => {
    return debts.reduce((summary, debt) => {
      const monthlyInterestRate = (debt.apr || 0) / 100 / 12;
      const monthlyInterest = debt.current * monthlyInterestRate;
      
      return {
        totalOriginalDebt: summary.totalOriginalDebt + debt.original,
        totalCurrentDebt: summary.totalCurrentDebt + debt.current,
        totalMonthlyInterest: summary.totalMonthlyInterest + monthlyInterest,
        totalMonthlyPayments: summary.totalMonthlyPayments + (debt.monthlyPayment || 0),
        totalInterestPaid: summary.totalInterestPaid + (debt.totalInterestPaid || 0)
      };
    }, {
      totalOriginalDebt: 0,
      totalCurrentDebt: 0,
      totalMonthlyInterest: 0,
      totalMonthlyPayments: 0,
      totalInterestPaid: 0
    });
  };

  /* ─── field updaters --------------------------------------------------- */
  const updateIncomeData = (i, k, v) => {
    setIncomeData((d) => {
      const c = [...d];
      c[i] = { ...c[i], [k]: parseFloat(v) || 0 };
      return c;
    });
  };
  
  // Helper for net income tab - update total income for a row
  const updateIncomeTotal = (rowIndex, totalValue) => {
    setIncomeData((currentData) => {
      const updated = [...currentData];
      if (updated[rowIndex]) {
        // Distribute the total proportionally across existing columns, or set to salary if all are zero
        const currentTotal = incomeColumns.reduce((sum, col) => sum + (updated[rowIndex][col] || 0), 0);
        
        if (currentTotal === 0) {
          // If no existing values, put everything in the first column
          const newRow = { ...updated[rowIndex] };
          incomeColumns.forEach(col => newRow[col] = 0);
          newRow[incomeColumns[0]] = parseFloat(totalValue) || 0;
          newRow.total = parseFloat(totalValue) || 0;
          updated[rowIndex] = newRow;
        } else {
          // Distribute proportionally
          const ratio = (parseFloat(totalValue) || 0) / currentTotal;
          const newRow = { ...updated[rowIndex] };
          incomeColumns.forEach(col => {
            newRow[col] = (newRow[col] || 0) * ratio;
          });
          newRow.total = parseFloat(totalValue) || 0;
          updated[rowIndex] = newRow;
        }
      }
      return updated;
    });
  };

  const updateExpenseData = (i, k, v) => {
    setExpenseData((d) => {
      const c = [...d];
      c[i] = { ...c[i], [k]: parseFloat(v) || 0 };
      return c;
    });
  };
  
  // Helper for net income tab - update total expenses for a row
  const updateExpenseTotal = (rowIndex, totalValue) => {
    setExpenseData((currentData) => {
      const updated = [...currentData];
      if (updated[rowIndex]) {
        // Distribute the total proportionally across existing columns, or set to first column if all are zero
        const currentTotal = expenseColumns.reduce((sum, col) => sum + (updated[rowIndex][col] || 0), 0);
        
        if (currentTotal === 0) {
          // If no existing values, put everything in the first column
          const newRow = { ...updated[rowIndex] };
          expenseColumns.forEach(col => newRow[col] = 0);
          newRow[expenseColumns[0]] = parseFloat(totalValue) || 0;
          newRow.total = parseFloat(totalValue) || 0;
          updated[rowIndex] = newRow;
        } else {
          // Distribute proportionally
          const ratio = (parseFloat(totalValue) || 0) / currentTotal;
          const newRow = { ...updated[rowIndex] };
          expenseColumns.forEach(col => {
            newRow[col] = (newRow[col] || 0) * ratio;
          });
          newRow.total = parseFloat(totalValue) || 0;
          updated[rowIndex] = newRow;
        }
      }
      return updated;
    });
  };
  const updateGoal = (i, k, v) => {
    setGoals((d) => {
      const c = [...d];
      c[i] = { ...c[i], [k]: k === 'name' ? v : parseFloat(v) || 0 };
      return c;
    });
  };
  const updateDebt = (i, k, v) => {
    setDebts((d) => {
      const c = [...d];
      
      // Handle string vs number fields
      if (k === 'name') {
        c[i] = { ...c[i], [k]: v };
      } else {
        // For numeric fields, ensure they're properly parsed
        const numValue = parseFloat(v) || 0;
        c[i] = { ...c[i], [k]: numValue };
        
        // Optional: Add validation for APR (typically 0-30%)
        if (k === 'apr' && numValue > 50) {
          console.warn('APR seems unusually high. Please verify.');
        }
        
        // Optional: Ensure monthly payment isn't negative
        if (k === 'monthlyPayment' && numValue < 0) {
          c[i] = { ...c[i], [k]: 0 };
        }
      }
      
      return c;
    });
  };
  const updateEmergencyFund = (k, v) =>
    setEmergencyFund((e) => ({ ...e, [k]: parseFloat(v) || 0 }));
  const updateInvestment = (i, k, v) => {
    setInvestments((d) => {
      const c = [...d];
      c[i] = { ...c[i], [k]: ['name', 'type'].includes(k) ? v : parseFloat(v) || 0 };
      return c;
    });
  };

  /* ─── derived helpers -------------------------------------------------- */
  const netIncomeData = incomeData.map((inc, idx) => {
    const exp = expenseData[idx] ?? { total: 0 };
    return { month: inc.month, income: inc.total, expenses: exp.total, netIncome: inc.total - exp.total };
  });
  
  // Filter out deleted investments for calculations
  const activeInvestments = investments.filter(inv => !inv.name.includes('[DELETED]'));
  const totalInvestments = activeInvestments.reduce((s, i) => s + i.amount, 0);
  /* ARC-61: the same total over the last SERVER-CONFIRMED portfolio. The
     Investments tab's charts and headline figure read this one, so a member is
     never shown a portfolio value that was not persisted. Undefined until a
     load confirms what the server holds - the tab falls back to the live total
     then, which is the pre-ARC-61 behaviour. */
  const committedActiveInvestments = Array.isArray(committedInvestments?.investments)
    ? committedInvestments.investments.filter(inv => !inv.name.includes('[DELETED]'))
    : null;
  const committedTotalInvestments = committedActiveInvestments
    ? committedActiveInvestments.reduce((s, i) => s + i.amount, 0)
    : undefined;
  const totalDebts       = debts.reduce((s, d) => s + d.current, 0);

  /* ─── navigation tabs -------------------------------------------------- */
  // Research tab is feature-flagged off by default. Admin flips
  // researchConfig/featureFlags.researchTabEnabled in the Firebase Console
  // to expose it. Ships dark.
  const TABS = [
    { id: 'overview',    label: 'Overview',       icon: TrendingUp },
    { id: 'income',      label: 'Income',         icon: DollarSign },
    { id: 'expenses',    label: 'Expenses',       icon: CreditCard },
    { id: 'netincome',   label: 'Net Income',     icon: TrendingUp },
    { id: 'debt',        label: 'Debt',           icon: CreditCard },
    { id: 'investments', label: 'Investments',    icon: Briefcase },
    { id: 'wallets',     label: 'Wallets',        icon: Wallet },
    // ONE entry for this feature in both flag states: the preview card while
    // strategyAutomationTabEnabled is off (what members see today), the real
    // tab once it is armed. See renderActiveTab below.
    { id: 'strategyautomation', label: 'Automated Strategies', icon: Zap },
    ...(isSupernovaTabEnabled()
      ? [{ id: 'supernova', label: 'Supernova Staking', icon: Star }]
      : []),
    { id: 'dca',         label: 'DCA',            icon: Shield },
    { id: 'goals',       label: 'Goals',          icon: Target },
    { id: 'retirement',  label: 'Retirement',     icon: Briefcase },
    { id: 'tools',       label: 'Tools',          icon: Sparkles },
    { id: 'riskprofile', label: 'Risk Profile',   icon: Gauge },
    ...(researchFlags.researchTabEnabled
      ? [{ id: 'research', label: 'Research',     icon: Search }]
      : []),
    { id: 'profile',     label: 'Profile',        icon: User },
    { id: 'print',       label: 'Print',          icon: Printer },
  ];
  /* ─── tab renderer ----------------------------------------------------- */
  const renderActiveTab = (id) => {
    switch (id) {
      case 'overview':
      return (
        <OverviewTab
          saving={saving}
          lastSaved={lastSaved}
          saveUserData={saveUserData}
          error={error}
          incomeData={incomeData}
          netWorth={netWorth}
          totalDebts={totalDebts}
          totalInvestments={totalInvestments}
          emergencyFund={emergencyFund}
          netIncomeData={netIncomeData}
          goals={goals}
          investments={activeInvestments}
          debts={debts}
          expenseData={expenseData}
          wallets={wallets}
          chartsReady={chartsReady}
          debtSummary={getDebtSummary()}
        />
      );

      case 'income':
        return (
          <IncomeTab
            incomeData={incomeData}
            incomeColumns={incomeColumns}
            editMode={editMode}
            onToggleEdit={() => setEditMode(!editMode)}
            updateIncomeData={updateIncomeData}
            onUpdateColumns={updateIncomeColumns}
            addNextMonth={addNextMonth}
            deleteMonth={deleteMonth}  // 👈 shared
            setIncomeData={setIncomeData}
            chartsReady={chartsReady}
            /* ARC-58: widgets render committed state; Save resolves explicitly;
               a failed read makes the tab read-only. */
            committedIncomeData={committedIncome?.incomeData}
            committedIncomeColumns={committedIncome?.incomeColumns}
            hasUnsavedChanges={incomeDirty}
            onSaveIncome={saveUserData}
            loadError={incomeLoadError}
          />
        );

      case 'expenses':
        return (
          <ExpensesTab
            expenseData={expenseData}
            expenseColumns={expenseColumns}
            editMode={editMode}
            onToggleEdit={() => setEditMode(!editMode)}
            updateExpenseData={updateExpenseData}
            addNextMonth={addNextMonth}
            deleteMonth={deleteMonth}  // 👈 shared
            setExpenseData={setExpenseData}
            onUpdateColumns={updateExpenseColumns}
            /* ARC-14: widgets render committed state; Save resolves explicitly. */
            committedExpenseData={committedExpenses?.expenseData}
            committedExpenseColumns={committedExpenses?.expenseColumns}
            hasUnsavedChanges={expensesDirty}
            onSaveExpenses={saveUserData}
          />
        );

      case 'netincome':
        return (
          <NetIncomeTab 
            netIncomeData={netIncomeData} 
            chartsReady={chartsReady}
            onUpdateIncomeData={updateIncomeTotal}
            onUpdateExpenseData={updateExpenseTotal}
            addNextMonth={addNextMonth}
            deleteMonth={deleteMonth}

          />
        );

      case 'goals':
        return (
          <GoalsTab
            goals={goals}
            editMode={editMode}
            onToggleEdit={() => setEditMode(!editMode)}
            updateGoal={updateGoal}
            investments={activeInvestments}
            totalInvestments={totalInvestments}
            totalDebts={totalDebts}
            expenseData={expenseData}
            wallets={wallets}
          />
        );

      case 'debt':
        return (
          <DebtTab
            debts={debts}
            editMode={editMode}
            onToggleEdit={() => setEditMode(!editMode)}
            updateDebt={updateDebt}
            addDebt={addDebt}
            removeDebt={removeDebt}
            chartsReady={chartsReady}
            /* ARC-60: widgets render committed state; Save resolves explicitly;
               a failed read makes the tab read-only. */
            committedDebts={committedDebts?.debts}
            hasUnsavedChanges={debtsDirty}
            onSaveDebts={saveUserData}
            loadError={debtLoadError}
          />
        );

      case 'investments':
        return (
          <InvestmentsTab
            investments={investments}
            totalInvestments={totalInvestments}
            editMode={editMode}
            onToggleEdit={() => setEditMode(!editMode)}
            updateInvestment={updateInvestment}
            chartsReady={chartsReady}
            addInvestment={addInvestment}
            removeInvestment={removeInvestment}
            investmentCategories={investmentCategories}
            onUpdateCategories={updateInvestmentCategories}
            investmentHistory={investmentHistory}
            updateInvestmentHistory={updateInvestmentHistory}
            wallets={wallets}
            /* ARC-61: widgets render committed state; Save resolves explicitly;
               a failed read makes the tab read-only. */
            committedInvestments={committedInvestments?.investments}
            committedInvestmentHistory={committedInvestments?.investmentHistory}
            committedTotalInvestments={committedTotalInvestments}
            hasUnsavedChanges={investmentsDirty}
            onSaveInvestments={saveUserData}
            loadError={investmentLoadError}
          />
        );

      case 'wallets':
        return (
          <WalletsTab
            wallets={wallets}
            setWallets={setWallets}
            /* ARC-61: a failed read makes the tab read-only. Every wallet
               action here writes straight into the save path, so refusing to
               edit is the same decision as refusing to save. */
            loadError={walletLoadError}
            hasUnsavedChanges={walletsDirty}
          />
        );

      case 'strategyautomation':
        // Always reachable ("Automated Strategies"), but what it renders is
        // feature-flagged. Flag off — the default, and every production build
        // — is the preview card. Flag on is the real tab, which also
        // self-gates, so nothing leaks while the flag resolves.
        if (strategyAutomationLoading) {
          return (
            <div className="surface p-6 sm:p-8 flex items-center gap-3 text-arc-muted">
              <Loader className="animate-spin text-arc-gold" size={18} />
              <span className="text-sm">Loading Strategy Automation…</span>
            </div>
          );
        }
        return strategyAutomationEnabled
          ? <StrategyAutomationTab />
          : <ComingSoonCard />;

      case 'supernova':
        // Feature-flagged (env: REACT_APP_SUPERNOVA_TAB_ENABLED, default OFF).
        // Hidden from nav unless the flag is on, and SupernovaTab also
        // self-gates before any hook mounts, so a stale persisted activeTab
        // exposes nothing — and reads no chain data.
        return <SupernovaTab />;

      case 'dca':
        return (
          <DCATab
            netIncomeData={netIncomeData}
            dcaData={dcaData}
            setDcaData={setDcaData}
          />
        );

      case 'retirement':
        return (
          <RetirementTab
            userAge={userAge}
            setUserAge={setUserAge}
            dob={dob}
            setDob={setDob}
            netWorth={netWorth}
            investments={investments}
            wallets={wallets}
            netIncomeData={netIncomeData}
            dcaData={dcaData}
            investmentHistory={investmentHistory}
            retirementSettings={retirementSettings}
            setRetirementSettings={setRetirementSettings}
          />
        );

      case 'tools':
        return <ToolsTab />;

      case 'research':
        // Feature-flagged. The TAB itself is hidden from nav unless the
        // flag is on, but we guard the case here defensively in case the
        // user has a stale activeTab in their persisted state.
        return researchFlags.researchTabEnabled
          ? (
            <ResearchTab
              initialFilter={pendingResearchFilter}
              onFilterConsumed={() => setPendingResearchFilter(null)}
            />
          )
          : <ToolsTab />;

      case 'profile':
        return (
          <ProfileTab emergencyFund={emergencyFund} />
        );

      case 'riskprofile':
        return (
          <RiskAssessmentTab
            questions={questions}
            calculateResults={calculateResults}
            QuestionSlider={QuestionSlider}
            ProgressBar={ProgressBar}
            CryptoReportView={CryptoReportView}
            logo={logo}
            riskAssessmentData={riskAssessmentData}
            setRiskAssessmentData={setRiskAssessmentData}
            /* ARC-59 */
            loadError={riskLoadError}
            hasSavedAssessment={isCompletedAssessment(committedRisk?.riskAssessmentData)}
            onRetakeAcknowledged={() => { riskRetakeAckRef.current = true; }}
          />
        );

      case 'print':
        return (
          <PrintView
            incomeData={incomeData}
            netIncomeData={netIncomeData}
            netWorth={netWorth}
            totalDebts={totalDebts}
            goals={goals}
            investments={activeInvestments}
            totalInvestments={totalInvestments}
          />
        );

      default:
        return null;
    }
  };

  /* ─── render ----------------------------------------------------------- */
  if (loading) {
    return (
      <div className="min-h-screen mesh flex items-center justify-center">
        <div className="text-center px-6">
          <div className="inline-flex items-center gap-2 mb-6">
            <span className="pulse-dot" aria-hidden="true" />
            <span className="eyebrow">Initializing</span>
          </div>
          <Loader className="animate-spin text-arc-gold mx-auto mb-6" size={40} strokeWidth={1.5} />
          <h2 className="headline text-2xl mb-2">Loading your dashboard</h2>
          <p className="text-arc-muted text-sm">Fetching your financial data from the chain…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen mesh">
      <Header
        user={user}
        onLogout={requestLogout}
        lastUpdated={lastSaved || new Date()}
        theme={theme}
        onToggleTheme={toggleTheme}
      />

      {/*
       * Two-column layout: SidebarNav on the left (fixed on lg+,
       * slide-in drawer on smaller screens), main content on the right.
       *
       * The sidebar replaced an earlier horizontal tab bar that
       * required scrolling and buried tabs like Research at the
       * overflow end. See SidebarNav.js for the responsive behavior.
       */}
      <div className="flex">
        <SidebarNav
          tabs={TABS}
          activeTab={activeTab}
          onTabChange={requestTabChange}
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />

        <div className="flex-1 min-w-0">
          {/* Mobile-only top bar with hamburger trigger. Hidden on lg+
              where the sidebar is permanently visible. */}
          <div className="lg:hidden sticky top-0 z-30 bg-arc-bg/80 backdrop-blur-md border-b border-arc-line px-4 py-3 flex items-center gap-3">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="btn-tactile w-9 h-9 rounded-lg border border-arc-line bg-arc-surface/60 flex items-center justify-center text-arc-ink hover:border-arc-gold/40 transition-all"
              aria-label="Open navigation"
            >
              <Menu size={16} />
            </button>
            <p className="text-sm font-semibold text-arc-ink capitalize">
              {TABS.find((t) => t.id === activeTab)?.label || ""}
            </p>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
            {/* body */}
            {renderActiveTab(activeTab)}
          </div>
        </div>
      </div>

      {/* print-specific CSS (inline for Cloud Print) */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
            @media print {
              .no-print { display:none!important }
              body { print-color-adjust:exact; -webkit-print-color-adjust:exact; background:#fff!important; color:#000!important }
              .mesh { background:#fff!important }
              .glass { background:#fff!important; border-color:#e5e7eb!important; backdrop-filter:none!important }
              .print\\:shadow-none { box-shadow:none!important }
              .print\\:border { border:1px solid #e5e7eb!important }
              .print\\:space-y-4>*+* { margin-top:1rem!important }
            }
          `,
        }}
      />
    </div>
  );
};

export default FinancialDashboard;